import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import cn from '@/utils/cn';
import { stripExecutable } from '@/utils/stripExecutable';

export function Section({
  title,
  children,
  gridClassName,
  action,
}: {
  title: string;
  children: ReactNode;
  gridClassName?: string;
  // Rendered on the heading row, flush right and centred against the title -
  // for a control that acts on this section alone (Fetch from GST on Business).
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h4>
        {action}
      </div>
      {/* A gridClassName that changes the column count MUST declare one for every
          breakpoint listed here (sm and xl): twMerge only dedupes classes sharing
          the same variant prefix, so e.g. `md:grid-cols-6` leaves xl:grid-cols-3
          in place and the grid silently narrows again at >=1280px. */}
      <div className={cn('grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3', gridClassName)}>
        {children}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  className?: string;
  readOnly?: boolean;
  maxLength?: number;
  // When set, restricts input to digits only, capped at maxLength (for phone
  // numbers). The keypad is hinted numeric too.
  digitsOnly?: boolean;
  // Field had no error slot at all, so inline validation had nowhere to render.
  error?: string;
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  className,
  readOnly,
  maxLength,
  digitsOnly,
  error,
}: FieldProps) {
  const fieldId = useId();
  const errorId = `${fieldId}-error`;
  const handleChange = (raw: string): void => {
    if (!digitsOnly) {
      onChange(raw);
      return;
    }
    const digits = raw.replace(/\D/g, '');
    onChange(maxLength ? digits.slice(0, maxLength) : digits);
  };

  return (
    <div className={cn('grid min-w-0 gap-1.5', className)}>
      <Label htmlFor={fieldId} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={fieldId}
        type={type}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        readOnly={readOnly}
        maxLength={maxLength}
        inputMode={digitsOnly ? 'numeric' : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn('min-w-0', readOnly && 'cursor-not-allowed bg-muted/50')}
      />
      {error && (
        <p id={errorId} role="alert" className="break-words text-xs leading-snug text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export function InfoCell({
  label,
  value,
  widthClass = 'max-w-[220px]',
}: {
  label: string;
  value: string;
  widthClass?: string;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col', widthClass)}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold [overflow-wrap:anywhere]">{value}</span>
    </div>
  );
}

export function Kv({ label, v }: { label: string; v: string }) {
  return (
    <div className="flex min-w-0 flex-col rounded-md border border-border bg-muted/30 px-3 py-2">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="break-words text-sm font-medium">{v || '-'}</span>
    </div>
  );
}

export function SummaryCard({
  icon,
  title,
  children,
  bodyClassName,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-background p-3">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {icon} {title}
      </h4>
      {/* Default stacks the cells; a full-width card can pass a grid/flex-wrap
          class to lay them out horizontally across the available space (Fix 6). */}
      <div className={cn('flex flex-col gap-2', bodyClassName)}>{children}</div>
    </div>
  );
}

export function LinkChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </span>
  );
}

interface EmailPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subject: string;
  bodyHtml: string;
}

// Injected into the preview iframe only, never into the sent email.
//
// The frame is sized to its content and the dialog does the scrolling, so the
// frame itself must not scroll: a second scrollbar inside the first is the thing
// it looked like (and it moved the email under a bar the reader did not ask for).
// `overflow:hidden` here plus `scrolling="no"` on the element covers both the
// document and the frame. The thin-thumb rules are kept for the rare case a
// client's own styles force a scroller anyway.
const PREVIEW_FRAME_CSS = `<style>
  html, body { overflow: hidden !important; }
  ::-webkit-scrollbar { width: 10px; height: 10px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background-color: #D4D4D4; border-radius: 9999px; border: 2px solid transparent; background-clip: content-box; }
  ::-webkit-scrollbar-thumb:hover { background-color: #B8B8B8; }
  ::-webkit-scrollbar-button { display: none !important; width: 0 !important; height: 0 !important; }
</style>`;

// The logo <img> the server writes into every email body, with or without an
// origin (mirrors baseLayout.service#LOGO_URL_PATTERN on the server).
const LOGO_URL_PATTERN =
  /(?:(?:https?:)?\/\/[^"'\s>]*)?\/api\/v1\/assets\/(?:advisorkhoj|email-logo)\.png/g;

/**
 * Points the preview's logo at the API THIS CLIENT talks to.
 *
 * The server builds that src from APP_PUBLIC_URL, which is the public origin of
 * the API as the outside world reaches it - correct for a real send, and often
 * unreachable from the browser doing the previewing (a production URL while
 * developing, a host behind DNS that has not moved yet). The result is a broken
 * image in every preview, which reads as "the logo is missing from our emails".
 *
 * Rewritten ONLY for the preview iframe. The composer's bodyHtml is untouched:
 * it is what gets sent, and email.service swaps that same hosted URL for the
 * inline `cid:` attachment at send time. A data: URI or a client-local origin
 * reaching the sent mail would break the logo for the recipient instead.
 *
 * VITE_API_BASE_URL can be absolute ('http://localhost:3000/api/v1') or relative
 * ('/api/v1' behind the nginx proxy); both are handled, and a relative one
 * resolves against the parent page, which is the origin serving that proxy.
 */
const withReachableLogo = (html: string): string => {
  const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return html.replace(LOGO_URL_PATTERN, () => `${apiBase}/assets/advisorkhoj.png`);
};

// Shared by every wizard step's email composer (StepLeadConfirmation,
// StepBillingNotice) - renders the sender's current subject/body exactly as
// the server would send it (bodyHtml already includes the branded layout,
// see email.service.ts#previewEmail).
export function EmailPreviewDialog({
  open,
  onOpenChange,
  subject,
  bodyHtml,
}: EmailPreviewDialogProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState(480);

  // The sandboxed iframe can swallow Escape when its content has focus, so the
  // Radix dialog's own Esc-to-close doesn't fire - close it from here too.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // Keyed on `open`, not mount-only. Radix unmounts DialogContent on close, so a
  // mount-only cleanup left an observer bound to a destroyed iframe document on
  // every open/close cycle, and `height` kept the previous email's value - the
  // next preview opened at the wrong size until its onLoad fired. Resetting the
  // height means each preview starts from the same neutral state.
  useEffect(() => {
    if (open) return undefined;
    observerRef.current?.disconnect();
    observerRef.current = null;
    setHeight(480);
    return undefined;
  }, [open]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // Size the iframe to the email card itself so the dialog has ONE scrollbar
  // (its own) instead of a fixed 70vh frame that left a long empty tail below
  // short emails and a nested scrollbar on long ones. Measured off
  // `.ak-container` (baseLayout.html's card), not documentElement: an iframe's
  // <html> is stretched to the frame's height, so measuring it would ratchet
  // the height upward and never shrink.
  //
  // `body.scrollHeight` is taken alongside it and the LARGER wins. The card is
  // the right thing to measure but not always the tallest thing in the document
  // - a template whose body padding or a stray trailing element sits outside it
  // left the frame a few pixels short, which is all it takes for the frame to
  // grow its own scrollbar. body is safe to read here (unlike <html>) because
  // baseLayout.html gives it no height, so it is exactly its content.
  const handleLoad = (): void => {
    const doc = iframeRef.current?.contentWindow?.document;
    const card = doc?.querySelector('.ak-container') ?? doc?.body;
    if (!card || !doc?.body) return;
    observerRef.current?.disconnect();
    const update = (): void =>
      setHeight(
        Math.max(240, Math.ceil(card.getBoundingClientRect().height), doc.body.scrollHeight),
      );
    update();
    // Both, because either can be the one that changes: the card when a block is
    // added or removed, body when a late-loading image (the logo) reflows it.
    const observer = new ResizeObserver(update);
    observer.observe(card);
    observer.observe(doc.body);
    observerRef.current = observer;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] flex-col overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Email preview</DialogTitle>
          <DialogDescription>
            Rendered exactly as it will be sent (subject, then body).
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm font-semibold">{subject}</p>
        <iframe
          ref={iframeRef}
          title="Email body preview"
          srcDoc={`${PREVIEW_FRAME_CSS}${withReachableLogo(stripExecutable(bodyHtml))}`}
          onLoad={handleLoad}
          scrolling="no"
          // allow-same-origin and NOTHING else, so handleLoad can measure the
          // height. NEVER add allow-scripts: the pair lets the frame drop its own
          // sandbox and reach the parent DOM. The body is also run through
          // stripExecutable above so there is nothing to execute either way.
          sandbox="allow-same-origin"
          style={{ height, overflow: 'hidden' }}
          className="w-full shrink-0 rounded-md border border-border bg-white"
        />
      </DialogContent>
    </Dialog>
  );
}
